/**
 * DailyMessageBanner — admin-broadcast message across the top of the homepage.
 * Admins get an inline edit affordance ("edit" pencil → prompts for new text).
 * Hides itself when there's no message set. Non-admins can only read.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Megaphone, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function DailyMessageBanner() {
  const [msg, setMsg] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  useEffect(() => { api.get("/site-message").then((r) => setMsg(r.data.message || "")).catch(() => setMsg("")); }, []);
  if (msg === null) return null;

  const edit = async () => {
    const next = window.prompt("Daily message for the homepage (max 280 chars). Leave empty to clear.", msg || "");
    if (next === null) return;  // cancelled
    try {
      await api.put("/admin/site-message", { message: next });
      setMsg(next);
      toast.success(next ? "Message updated" : "Message cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!msg && !isAdmin) return null;

  return (
    <div data-testid="daily-message-banner" className="bg-[#FF4500] text-black border-b border-black/20 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-2.5 flex items-center gap-3">
        <Megaphone className="h-4 w-4 shrink-0" />
        {msg ? (
          <div className="flex-1 overflow-hidden">
            <div className="daily-marquee flex whitespace-nowrap gap-16">
              {/* Duplicated content for a seamless loop. Each block is aria-hidden
                  on the copy so screen readers only read the message once. */}
              <span data-testid="daily-message-text" className="font-mono text-xs md:text-sm font-bold">
                {msg}
              </span>
              <span className="font-mono text-xs md:text-sm font-bold" aria-hidden="true">
                {msg}
              </span>
              <span className="font-mono text-xs md:text-sm font-bold" aria-hidden="true">
                {msg}
              </span>
            </div>
          </div>
        ) : (
          <div className="font-mono text-xs italic flex-1 opacity-70">No daily message set — click edit to broadcast one.</div>
        )}
        {isAdmin && (
          <button
            data-testid="daily-message-edit"
            onClick={edit}
            className="h-7 px-2 grid place-items-center bg-black/20 hover:bg-black/40 font-mono text-[10px] uppercase tracking-widest shrink-0"
            title="Edit daily message"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}
